import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  filter,
  map,
  publishReplay,
  refCount,
  withLatestFrom,
  tap,
  shareReplay
} from 'rxjs/operators';

import { CompanyDataDTO } from '../../settings/dto/company-data.dto';
import { CompanyData } from '../../settings/model/company-data.model';
import { CompanySettings } from '../../settings/model/company-settings';
import { RpcService } from '../http/rpc.service';
import { Utils } from '../utils/utils';
import { Establishment } from './model/establishment';
import * as fromCompanySettingsStore from '../../settings/store';
import { Store } from '@ngrx/store';
import { TokenService } from '../../authentication/token.service';
import { environment } from 'environments/environment';

@Injectable()
export class EstablishmentService {
  public establishmentsCache$: Observable<Array<Establishment>>;
  public establishments$: Observable<Array<Establishment>>;
  public establishmentChanged$: Observable<boolean>;
  private _selectedEstablishment: Establishment;
  private _establishmentChanged: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  private _establishmentsSubject: BehaviorSubject<
    Array<Establishment>
  > = new BehaviorSubject(null);

  constructor(
    private tokenService: TokenService,
    private rpcService: RpcService,
    private cookies: CookieService,
    private store: Store<fromCompanySettingsStore.CompanyState>,
    private router: Router
  ) {
    this.tokenService.tokenChanged$.subscribe((token: string) => {
      if (!token) {
        this.establishmentsCache$ = null;
      }
    });

    const navigationSource: Observable<string> = this.router.events.pipe(
      filter((event: NavigationEnd) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        const route: ActivatedRoute = Utils.findNested(
          this.router.routerState.root,
          'firstChild'
        );
        return (
          route.snapshot.data['switchAccountUrl'] ||
          event.url.replace(/^\//, '')
        );
      }),
      shareReplay(1)
    );

    this.establishments$ = this._establishmentsSubject.asObservable();
    this.establishmentChanged$ = this._establishmentChanged.asObservable();

    this.establishmentChanged$
      .pipe(
        filter(Boolean),
        tap(() => console.log('SHOULD SWITCH ROUTE>>>>>')),
        withLatestFrom(navigationSource)
      )
      .subscribe(([, route]: [string, string]) => {
        console.log('GOING TO SWITCH ROUTE>>>>>>>');
        if (!/login/.test(route)) {
          this.router.navigate(['switch-account', route]);
        }
      });
  }

  /**
   * Returns the establishment of a main company. Will only return the  company calling the function and his
   * establishments. If no establishments exist for this company, only the company itselfs is returned in the array.
   */
  // TODO: HOS-10017 - don't use mix of Observables and BehaviourSubjects
  public getEstablishments(
    forceReload: boolean = false
  ): Observable<Array<Establishment>> {
    if (!this.establishmentsCache$ || forceReload) {
      this.establishmentsCache$ = combineLatest(
        this.getEstablishmentsCall(),
        fromCompanySettingsStore.getSettingsFromStore(this.store)
      ).pipe(
        map((data: [Array<Establishment>, CompanySettings]) => {
          data[0].map((establishment: Establishment) => {
            establishment.isMaster =
              establishment.companyId === data[1].id &&
              data[0] &&
              data[0].length > 1;
            return establishment;
          });
          this._establishmentsSubject.next(data[0]);
          return data[0].sort((a, b) => a.name.localeCompare(b.name));
        })
      );
    }

    return this.establishmentsCache$;
  }

  /**
   * Returns the complete chain where the company is part of. The difference with #getEstablishments is that these
   * function can be used by establishments to find it siblings. If the company isn't part of a establishment chain
   * then only the company itself is returned in the array.
   */
  public getChain(): Observable<Array<CompanyData>> {
    return this.rpcService
      .makeManagerCall('getAllEstablishmentsOfChain', [])
      .pipe(
        map((companyData: Array<CompanyDataDTO>) =>
          companyData.map(CompanyData.fromDTO)
        ),
        publishReplay(1),
        refCount()
      );
  }

  public getTokenForEstablishment(companyId: number): Observable<string> {
    return this.rpcService.makeManagerCall(
      'getTokenForEstablishment',
      [companyId, 'webmanager'],
      this.tokenService.adminToken
    );
  }

  public getTokenForSwitchingToEstablishment(
    companyId: number
  ): Observable<string> {
    return this.rpcService.makeManagerCall(
      'getTokenForSwitchingToEstablishment',
      [companyId, 'webmanager'],
      this.tokenService.adminToken
    );
  }

  public getContextForEstablishment(companyId: number): Observable<string> {
    return this.rpcService.makeManagerCall('getRestoContext', [
      companyId,
      'webmanager'
    ]);
  }

  public changeEstablishment(establishment: Establishment): void {
    this.selectedEstablishment = establishment;
    this.removeCookies();
    if (environment.enableSSO) {
      if (establishment.isMaster) {
        this.tokenService.context = null;
        this._establishmentChanged.next(true);
      } else {
        this.getContextForEstablishment(establishment.companyId).subscribe(
          (context: string) => {
            this.tokenService.context = context;
            this._establishmentChanged.next(true);
          }
        );
      }
    } else {
      this.getTokenForSwitchingToEstablishment(
        establishment.companyId
      ).subscribe(token => {
        this.tokenService.token = token;
        this._establishmentChanged.next(true);
      });
    }
  }

  private getEstablishmentsCall(): Observable<Array<Establishment>> {
    return this.rpcService.makeManagerCall('getCompanyEstablishments').pipe(
      map((establishments: [Object]) => {
        const fetchedEstablishments: Array<Establishment> = establishments.map(
          Establishment.fromDto
        );
        this.selectedEstablishment = fetchedEstablishments[0];
        return fetchedEstablishments;
      }),
      publishReplay(1),
      refCount()
    );
  }

  public set selectedEstablishment(establishment: Establishment) {
    this._selectedEstablishment = establishment;
    sessionStorage.setItem(
      'selectedEstablishment',
      establishment.companyId.toString()
    );
  }

  public get selectedEstablishment(): Establishment {
    return this._selectedEstablishment;
  }

  private removeCookies(): void {
    this.cookies.remove('email');
    this.cookies.remove('company');
    this.cookies.remove('country');
    this.cookies.remove('companyObject');
    this.cookies.remove('openingstime');
    this.cookies.remove('closingtime');
    this.cookies.remove('vatInclusive');
    this.cookies.remove('accountText');
    this.cookies.remove('account');
    this.cookies.remove('lastAccountCheck');
    this.cookies.remove('enforceStock');
  }
}
